import { useTranslation } from 'react-i18next';
// Renderiza cards estructuradas que la IA puede devolver en structured_payload.cards.
// Cada card: { type: 'vehicle_summary'|'ticket'|'task'|'sensor'|'receiver'|'report_insight'|'recommendation', ... }
import React from 'react';
import { Paper, Typography, Stack, Chip, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InsightsIcon from '@mui/icons-material/Insights';

const SEVERITY_COLOR = { critical: 'error', extreme: 'error', warning: 'warning', alarm: 'warning', info: 'info', none: 'default' };

export default function AIProposalCards({ payload }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cards = (payload && payload.cards) || [];
  if (!cards.length) return null;

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {cards.map((card, i) => <EntityCard key={i} card={card} navigate={navigate} />)}
    </Stack>
  );
}

function EntityCard({ card, navigate }) {
  const { t } = useTranslation();
  switch (card.type) {
    case 'vehicle_summary':
    case t('hardcoded.components_ai_aientitycards.vehicle'):
      return (
        <CardShell icon={<DirectionsCarIcon />} title={card.title || t('vehicles.table.vehicle')}
          chip={card.status} action={card.vehicle_id ? { label: t('hardcoded.components_ai_aientitycards.abrir_vehiculo'), to: '/vehicles' } : null} navigate={navigate}>
          {card.open_tickets != null && <Line label={t('hardcoded.components_ai_aientitycards.tickets_abiertos')} value={card.open_tickets} />}
          {card.recommendation && <Typography variant="body2" sx={{ mt: 0.5 }}>{card.recommendation}</Typography>}
        </CardShell>
      );
    case t('hardcoded.components_ai_aientitycards.ticket'):
      return (
        <CardShell icon={<ConfirmationNumberIcon />} title={card.ticket_number || card.title || t('hardcoded.components_ai_aientitycards.ticket')}
          chip={card.severity} chipColor={SEVERITY_COLOR[card.severity] || 'default'}
          action={card.id ? { label: 'Ver ticket', to: `/alarm-tickets/${card.id}` } : null} navigate={navigate}>
          {card.status && <Line label={t('gps.table.status')} value={card.status} />}
          {card.recommendation && <Typography variant="body2" sx={{ mt: 0.5 }}>{card.recommendation}</Typography>}
        </CardShell>
      );
    case t('hardcoded.components_ai_aientitycards.task'):
      return (
        <CardShell icon={<AssignmentIcon />} title={card.title || t('hardcoded.components_ai_aientitycards.tarea')}
          chip={card.status} action={card.id ? { label: 'Ver tarea', to: `/tasks/${card.id}` } : null} navigate={navigate} />
      );
    case 'report_insight':
    case t('hardcoded.components_ai_aientitycards.recommendation'):
      return (
        <CardShell icon={<InsightsIcon />} title={card.title || 'Insight'} chip={card.severity}
          chipColor={SEVERITY_COLOR[card.severity] || 'info'}>
          {card.recommendation && <Typography variant="body2">{card.recommendation}</Typography>}
        </CardShell>
      );
    default:
      return null;
  }
}

function CardShell({ icon, title, chip, chipColor = 'default', action, navigate, children }) {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: children ? 0.5 : 0 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
        {chip && <Chip size="small" label={chip} color={chipColor} sx={{ height: 20 }} />}
      </Stack>
      {children}
      {action && (
        <Button size="small" sx={{ mt: 0.5 }} onClick={() => navigate(action.to)}>{action.label}</Button>
      )}
    </Paper>
  );
}

function Line({ label, value }) {
  const { t } = useTranslation();
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}: <strong>{value}</strong>
    </Typography>
  );
}
